import { usePathname } from 'next/navigation';

import { ComponentType, ReactNode } from 'react';
import Countdown from 'react-countdown';

import {
  Button,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalDescription,
  ModalHeading,
  useModalController,
} from '@uikit';

import Amount from '@uikit/icons/Amount';
import Binance from '@uikit/icons/Binance';
import BinanceBlack from '@uikit/icons/BinanceBlack';
import Gift from '@uikit/icons/Gift';
import Timer from '@uikit/icons/Timer';
import Wallet from '@uikit/icons/Wallet';

import { BinanceCheckoutEntry, DeserializedHotOffer } from '@shared/api/types/hotOffers';

export interface HotOfferCheckoutModalProps {
  modalController: ReturnType<typeof useModalController>;
  offer: DeserializedHotOffer;
  binanceCheckoutEntry?: BinanceCheckoutEntry;
}

function formatDuration(time: string) {
  return time
    .split(':')
    .map((part) => part.padStart(2, '0'))
    .join(':');
}

export default function HotOfferCheckoutModal({
  offer,
  modalController,
  binanceCheckoutEntry,
}: HotOfferCheckoutModalProps) {
  const pathname = usePathname();
  const offerCurrency = offer?.attributes?.offerCurrency || 'USDT';
  const cashBackPercentage = offer?.attributes?.cashBackPercentage || 5;
  const expireTime = binanceCheckoutEntry?.attributes?.expireTime || 0;
  const universalUrl = binanceCheckoutEntry?.attributes?.universalUrl || '';
  const offerPrice = offer?.attributes?.offerPrice || 0;
  const orderDetails: {
    Icon: ComponentType;
    title: string;
    content: ReactNode;
  }[] = [
    {
      Icon: Amount,
      title: 'Total order amount:',
      content: `${offerPrice} $${offerCurrency}`,
    },
    {
      Icon: Gift,
      title: 'Cashback',
      content: `${cashBackPercentage}% (${
        ((offerPrice ?? 1) / 100) * (cashBackPercentage ?? 5)
      } $${offerCurrency})`,
    },
    {
      Icon: Wallet,
      title: 'Default wallet',
      content: (
        <div className="flex items-center text-neutral-700 gap-2">
          <Binance className="icon w-[90px]" /> PAY
        </div>
      ),
    },
    {
      Icon: Timer,
      title: 'Your order will expire soon:',
      content: (
        <Countdown
          date={new Date(expireTime)}
          renderer={(props) => (
            <span>{formatDuration(`${props.hours}:${props.minutes}:${props.seconds}`)}</span>
          )}
        />
      ),
    },
  ];

  return (
    <Modal {...modalController.props}>
      <ModalContent className="items-start gap-4">
        <ModalCloseButton />

        <ModalHeading className="m-0">Checkout</ModalHeading>

        <ModalDescription className="text-left m-0">
          You can pay with any coin in your Binance wallet with 0 fees.
          <br />
          <a
            className="text-neutral-700 font-bold"
            href="https://binance.pxf.io/magic?refcode=451265838"
            target="_blank"
            rel="noreferrer"
          >
            Create your Binance account now
          </a>
        </ModalDescription>

        <div className="flex flex-col gap-4 my-8">
          <p className="t-button text-neutral-500">Order details</p>

          <ul className="flex flex-col gap-4">
            {orderDetails.map(({ title, Icon, content }) => (
              <li key={title} className="flex items-center gap-4">
                <p>
                  <span className="icon text-secondary-600">
                    <Icon />
                  </span>
                </p>
                <p className="t-button text-neutral-500">{title}</p>
                <p className="text-secondary-600">{content}</p>
              </li>
            ))}
          </ul>
        </div>

        <Button
          variant="custom"
          as="a"
          className="h-[50px] w-full max-w-[360px] mx-auto bg-[#f0b90b] text-neutral-50 font-semibold gap-1"
          href={universalUrl || `${pathname}/?status=fail`}
        >
          Pay with <BinanceBlack className="icon w-[88px]" /> PAY
        </Button>
      </ModalContent>
    </Modal>
  );
}
